/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import {GridRowId} from "@mui/x-data-grid-pro";
import ExpandedGridRowsContext from "qqq/components/query/ExpandedGridRowsContext";
import React, {useContext, useState} from "react";

interface Props
{
   value: any;
   rowId: GridRowId;
   separator: string;
}


/*******************************************************************************
 ** A grid cell for list-like values ("a; b; c"): shown as-is on one line, with a
 ** small control at the right edge of the cell that expands it to one entry per
 ** line (and back).  Values with a single entry render as-is with no control.
 **
 ** The cell adds no vertical padding of its own: when expanded, the query screen
 ** marks the whole row (see getRowClassName in RecordQuery) and the row's css
 ** pads and top-aligns every cell alike, so this text, the other columns and the
 ** row checkbox all start on the same line.
 *******************************************************************************/
export default function MultiLineCell({value, rowId, separator}: Props): JSX.Element
{
   const expandedGridRows = useContext(ExpandedGridRowsContext);
   const [locallyExpanded, setLocallyExpanded] = useState(false);

   ///////////////////////////////////////////////////////////////////////////////////
   // only plain strings can be split; anything else (e.g. an element built by a    //
   // value formatter) is shown untouched rather than stringified to "[object ...]" //
   ///////////////////////////////////////////////////////////////////////////////////
   if (value != null && typeof value !== "string")
   {
      return (<>{value}</>);
   }

   const text = value ?? "";
   if (!separator || !text.includes(separator))
   {
      return (<>{text}</>);
   }

   const expanded = expandedGridRows.isProvided ? expandedGridRows.expandedRowIds.has(rowId) : locallyExpanded;

   ////////////////////////////////////////////////////////////////////////////////////////////
   // the control swallows mouse events at every stage, so a click on it can never double as //
   // a row click (which the query screen turns into navigation to the record)               //
   ////////////////////////////////////////////////////////////////////////////////////////////
   const swallow = (event: React.MouseEvent) =>
   {
      event.stopPropagation();
   };

   const toggle = (event: React.MouseEvent) =>
   {
      event.stopPropagation();
      event.preventDefault();
      if (expandedGridRows.isProvided)
      {
         expandedGridRows.toggleRow(rowId);
      }
      else
      {
         setLocallyExpanded(!locallyExpanded);
      }
   };

   const controlLabel = expanded ? "Show on one line" : "Show one per line";

   return (
      <Box display="flex" alignItems={expanded ? "flex-start" : "center"} width="100%" minWidth={0}>
         <Box flex="1" minWidth={0} whiteSpace={expanded ? "normal" : "nowrap"} overflow="hidden" textOverflow="ellipsis">
            {expanded
               ? text.split(separator).map((entry: string, index: number) => (
                  //////////////////////////////////////////////////////////////////////////////////
                  // one block per entry, with a hanging indent: an entry too long for the column //
                  // wraps under itself, so its continuation cannot be mistaken for a new entry   //
                  //////////////////////////////////////////////////////////////////////////////////
                  <Box key={index} sx={{textIndent: "-1em", paddingLeft: "1em"}}>{entry}</Box>
               ))
               : text}
         </Box>
         <IconButton size="small" onClick={toggle} onMouseDown={swallow} onMouseUp={swallow} onDoubleClick={swallow} aria-label={controlLabel} title={controlLabel} sx={{ml: "0.25rem", mr: "24px", p: "4px", mt: expanded ? "-0.25rem" : 0, flexShrink: 0}}>
            <Icon fontSize="small">{expanded ? "unfold_less" : "unfold_more"}</Icon>
         </IconButton>
      </Box>
   );
}
